import {
  put, takeLatest, select, all, take,
} from 'redux-saga/effects';
import _ from 'lodash';
import localStorage from 'local-storage';
import { TeammoApi } from 'app/utils/redux/helpers';
import history from 'app/routes/history';
import {
  CognitoSignUp,
  CognitoConfirmEmail,
  CognitoResendVerificationCode,
  CognitoLogin,
} from 'app/utils/aws/awsCognito';
import { OnBoardingActionTypes as types } from 'app/ducks/onboarding/types';
import { parseCognitoErrorMessage } from 'app/ducks/onboarding/selectors';
import { AuthenticationActionTypes } from 'app/ducks/authentication/types';

import {
  sagaActions,
  updateAdminData,
  loginUser,
} from 'app/ducks/onboarding/actions';
import { sagaActions as authSagaActions } from 'app/ducks/authentication/actions';
import { createConnection } from 'app/ducks/connections/actions';

function* createNewUserSaga(action): any {
  try {
    yield put(sagaActions.operationPending());
    const [firstName, lastName] = action.payload.fullName.split(' ');
    yield put(yield updateAdminData(action.payload));
    const newUser = yield select((state) => state.onBoarding.inFlightUser);
    const userInfo = {
      email: newUser.email,
      password: newUser.password,
      firstName,
      lastName,
      fullName: newUser.fullName,
      mobile: newUser.mobile,
      invitationId: newUser.invitationId,
      organisationId: newUser.organisationId,
    };

    const clientMetadata = {
      organisationId: newUser.organisationId,
      organisationName: newUser.organisation.organisationName,
      organisationSport: newUser.organisation.selectedSport,
      roleType: 'organisation-admin',
    };

    const cog = yield CognitoSignUp(userInfo, clientMetadata);
    if (!_.isEmpty(cog?.result?.username)) {
      localStorage.set('onboarding', { email: cog.result.username });
    }

    yield put(yield updateAdminData(newUser));
    yield put(sagaActions.userCreationSuccess());
    if (!_.isEmpty(action.payload?.provider)) {
      history.push({
        pathname: `/get-started/verify/connect/${action?.payload?.provider}`,
        data: {
          email: action.payload.email,
        },
      });
    } else {
      history.push({
        pathname: '/get-started/verify',
        data: {
          email: action.payload.email,
        },
      });
    }
  } catch (error) {
    if (error.code === 'UsernameExistsException') {
      try {
        yield CognitoResendVerificationCode(action.payload.email);
        yield put(sagaActions.userCreationSuccess());

        if (!_.isEmpty(action.payload?.provider)) {
          history.push({
            pathname: `/get-started/verify/connect/${action?.payload?.provider}`,
            data: {
              email: action.payload.email,
            },
          });
        } else {
          history.push({
            pathname: '/get-started/verify',
            data: {
              email: action.payload.email,
            },
          });
        }
      } catch (e) {
        yield put(
          sagaActions.userCreationFailed(
            parseCognitoErrorMessage(e.code, e.message),
          ),
        );
      }
    } else {
      yield put(
        sagaActions.userCreationFailed(
          parseCognitoErrorMessage(error.code, error.message),
        ),
      );
    }
  }
}

function* loginUserSaga(action): any {
  yield put(sagaActions.operationPending());
  yield put(authSagaActions.authenticateCognitoUserPending());
  const storedEmail = !_.isEmpty(localStorage.get('onboarding'))
    ? localStorage.get('onboarding').email
    : '';
  const email = action.payload.email.toLowerCase() || storedEmail.toLowerCase();
  const { password } = action.payload;
  if (!_.isEmpty(email) && !_.isEmpty(password)) {
    try {
      const response = yield CognitoLogin({
        email,
        password: action.payload.password,
      });
      // const { jwtToken } = response.idToken;
      yield put(
        authSagaActions.authenticateCognitoUserSuccess(
          response.signInUserSession,
          action.payload.organisationId,
        ),
      );
    } catch (error) {
      yield put(authSagaActions.authenticateCognitoUserFailed(error.message));
    }
  } else {
    history.push('/login');
  }
}

function* verifyUserSaga(action): any {
  const storedEmail = !_.isEmpty(localStorage.get('onboarding'))
    ? localStorage.get('onboarding').email
    : '';
  const email = action.payload?.user?.email.toLowerCase() || storedEmail.toLowerCase();
  try {
    yield put(sagaActions.operationPending());
    const newUser = yield select((state) => state.onBoarding.inFlightUser);

    const clientMetadata = {
      organisationId: newUser.organisationId,
      organisationName: newUser.organisation.organisationName,
      organisationSport: newUser.organisation.selectedSport,
      roleType: 'organisation-admin',
    };
    if (action.payload?.user?.provider === 'gameday') {
      clientMetadata.connectionGamedayId = action.payload.user?.providerInfo?.gamedayOrganisationId;
    }
    const response = yield CognitoConfirmEmail(
      email,
        action.payload?.user?.value,
        clientMetadata,
    );
    const inflightUser = yield select((state) => state.onBoarding.inFlightUser);
    yield put(loginUser(inflightUser));
    yield take(AuthenticationActionTypes.AUTHENTICATE_COGNITO_USER_SUCCESS);
    let { redirectLocation } = action.payload;
    if (action.payload?.user?.provider === 'gameday') {
      redirectLocation = '/welcome/gameday';
    }
    const redirect = redirectLocation !== undefined;

    if (redirect) {
      history.push(redirectLocation);
    } else {
      history.push('/welcome');
    }

    yield put(sagaActions.verificationSuccess(response.user));
  } catch (error) {
    yield put(sagaActions.verificationFailed({ pin: error.message }));
  }
}

function* createBankAccountSaga(action): any {
  yield put(sagaActions.operationPending());
  const [error, bank] = yield (yield TeammoApi(
    yield select(),
  )).bankAccounts.create({
    accountHolderName: action.payload.name,
    accountNumber: action.payload.number,
    routingNumber: action.payload.bsb,
    country: {
      id: 'AUS',
    },
    bankName: action.payload.bank,
  });

  if (error) {
    yield put(sagaActions.bankAccountCreationFailed(error.message));
  } else if (bank) {
    yield put(sagaActions.operationFinished());
    yield put(sagaActions.continueToDashboard());
    history.push('/dashboard');
  }
}

/* ********************************* SAGA WATCHERS ********************************* */

function* createNewUserSagaWatcher(): any {
  yield takeLatest(types.CREATE_NEW_USER_REQUESTED, createNewUserSaga);
}

function* loginUserSagaWatcher(): any {
  yield takeLatest(types.LOGIN_USER_REQUESTED, loginUserSaga);
}

function* verifyUserSagaWatcher(): any {
  yield takeLatest(types.VERIFY_USER_REQUESTED, verifyUserSaga);
}

function* createBankAccountSagaWatcher(): any {
  yield takeLatest(types.CREATE_BANK_ACCOUNT_REQUESTED, createBankAccountSaga);
}

export default function* root(): any {
  yield all([
    createNewUserSagaWatcher(),
    loginUserSagaWatcher(),
    verifyUserSagaWatcher(),
    createBankAccountSagaWatcher(),
  ]);
}
